// src/components/RetailerList.jsx
import { useEffect, useState, useMemo } from "react";
import { Search, Loader2, ArrowUpDown } from "lucide-react";
import { cn } from "../../components/utils/index"; // reuse your utils for money formatting
import { getRetailers } from "../../api/retailersApi";
import Navbar from "../../components/Navbar";
import { Link } from "react-router-dom";

// -----------------------------
// Table Header with sorting
// -----------------------------
function ThSortable({ children, active, dir, onToggle }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
    >
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1 hover:underline"
      >
        {children}
        <ArrowUpDown
          className={cn(
            "h-4 w-4 transition",
            active ? "opacity-100" : "opacity-40"
          )}
        />
      </button>
    </th>
  );
}

// -----------------------------
// Pagination
// -----------------------------
function Paginator({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
      <div className="text-sm text-gray-500">
        Page {page} of {pages} • {total.toLocaleString()} retailers
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows</label>
        <select
          className="rounded-xl border px-3 py-1.5 bg-white shadow-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            ◀
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            ▶
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------
// Main Retailers Table
// -----------------------------
export default function RetailerList() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const [total, setTotal] = useState(0);

  const qDeb = useMemo(() => q, [q]);

  async function fetchRetailers({ page, pageSize, q, sort }) {
    const params = { page, pageSize, q };
    if (sort?.field) {
      params.sortField = sort.field;
      params.sortDir = sort.dir;
    }

    const res = await getRetailers(params);
    return res;
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetchRetailers({ page, pageSize, q: qDeb, sort });
        if (alive) {
          setRetailers(r.data || []);
          setTotal(r.total || 0);
        }
      } catch (err) {
        console.error("Failed to fetch retailers:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, [page, pageSize, qDeb, sort]);

  const toggleSort = (field) => {
    setSort((prev) =>
      prev.field !== field
        ? { field, dir: "asc" }
        : { field, dir: prev.dir === "asc" ? "desc" : "asc" }
    );
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-2xl font-semibold mb-4">Retailers <span className="font-normal text-sm text-green-600">[Total - <span className="font-bold">{total}</span>]</span></h1>

        <div className="flex justify-end mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name, phone number, address..."
              className="w-72 rounded-2xl border bg-white pl-9 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        {/* Table */}
        <table className="min-w-full divide-y divide-gray-200 rounded-xl overflow-hidden shadow-lg">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <ThSortable
                active={sort.field === "brand"}
                dir={sort.dir}
                onToggle={() => toggleSort("brand")}
              >
                Name
              </ThSortable>
              <ThSortable
                active={sort.field === "name"}
                dir={sort.dir}
                onToggle={() => toggleSort("name")}
              >
                Phone Number
              </ThSortable>
              <ThSortable
                active={sort.field === "size_label"}
                dir={sort.dir}
                onToggle={() => toggleSort("size_label")}
              >
                Address
              </ThSortable>
              <ThSortable
                active={sort.field === "on_hand_bottles"}
                dir={sort.dir}
                onToggle={() => toggleSort("on_hand_bottles")}
              >
                Status
              </ThSortable>

            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                  Loading retailers..
                </td>
              </tr>
            )}

            {!loading && retailers.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  No retailers found.
                </td>
              </tr>
            )}

            {!loading &&
              retailers.map((p) => {
                return (
                  <tr
                    key={p.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {p.name}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {p.mobile_number || ""}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {p.route_name || ""}
                    </td>
                    <td className="px-4 py-3 text-sm text-green-600">ACTIVE</td>
                    <td className="px-4 py-3 text-sm"><Link className="text-blue-500 hover:underline" to={"/refrigerators/" + p.id}>Refrigerator</Link></td>

                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Pagination */}
        <Paginator
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
